import React from 'react';
import type { MealBreakConfig, RateMultiplier, Role } from '@/types';
import {
  CountModeOffsetGraphic,
  type CountModeOffsetGraphicProps,
} from '@/components/config_forms/shared/help-graphics/CountModeOffsetGraphic';
import type {
  OffsetBracket,
  OffsetLadderEntry,
} from '@/components/config_forms/shared/help-graphics/OffsetComparisonTimeline';
import {
  getStraightTimeMultiplier,
  getSortedOvertimeMultipliers,
  getThreshold,
  getSampleRole,
} from '@/utils/rateCalculations';
import { normalizeExtendedTiers } from '@/utils/extendedOvertime';
import { getOvertimeLadderInfo } from '@/hooks/useOvertimeLadderInfo';
import { ExtendedOvertimeSection } from '@/components/config_forms/shared/ExtendedOvertimeSection';
import { CONTROL_ROW_BODY_CLASS, CONTROL_ROW_LABEL_CLASS } from '@/components/config_forms/shared/ControlRow';

export interface MealCountOffsetInjectableParams {
  mealBreakConfig: MealBreakConfig;
  rateMultipliers: RateMultiplier[];
  /** Optional write-back. When undefined the editor renders read-only. */
  onMealConfigChange?: (updates: Partial<MealBreakConfig>) => void;
  /** Roles list — drives sample role's billingStructure. Optional — defaults to []. */
  roles?: Role[];
  /** Tier context for the Extended Overtime customized indicator. */
  context?: 'user' | 'client';
}

/**
 * Builds the `meal_count_offset_comparison` injectable for the count-mode
 * offset_overtime help sheet.
 *
 * Extended Overtime is permitted in count mode: it just adds rungs to the rate
 * ladder, which is orthogonal to whether the surcharge triggers on deadline or
 * miss count. The CHECK constraint at `2026_04_19_meal_count_offset_overtime.sql`
 * still forbids the time-gated escalation fields (`use_additional_offset`,
 * `step_up_after_minutes`) — those have no count-mode analog.
 *
 * Mirrors the deadline-mode helper (`buildLadderOffsetComparisonInjectable`)
 * for `maxOffset` and `offsetDisabled` — both gate on the base ladder length.
 */
export function buildMealCountOffsetInjectable({
  mealBreakConfig,
  rateMultipliers,
  onMealConfigChange,
  roles = [],
  context = 'user',
}: MealCountOffsetInjectableParams): {
  injectables: Record<string, React.ReactNode>;
} {
  const sampleRole = getSampleRole(roles);
  const billingStructure = sampleRole.billingStructure;

  const straightTime = getStraightTimeMultiplier(rateMultipliers);
  const overtimeMultipliers = getSortedOvertimeMultipliers(
    rateMultipliers,
    billingStructure,
  );
  const allMultipliers: RateMultiplier[] = [straightTime, ...overtimeMultipliers];

  // Prefer day_rate thresholds when overtime multipliers carry per-shift
  // thresholds; otherwise fall back to hourly. Mirrors getSortedOvertimeMultipliers.
  const ladderBillingStructure: 'day_rate' | 'hourly' =
    overtimeMultipliers.length > 0 &&
    overtimeMultipliers[0].defaultPerShiftThresholdHours != null
      ? 'day_rate'
      : 'hourly';

  const brackets: OffsetBracket[] = allMultipliers.map((m, i) => {
    const threshold = getThreshold(m, ladderBillingStructure);
    const nextThreshold =
      i + 1 < allMultipliers.length
        ? getThreshold(allMultipliers[i + 1], ladderBillingStructure)
        : threshold + 4;
    return {
      flex: Math.max(nextThreshold - threshold, 1),
      naturalMultiplier: m.defaultMultiplier ?? 0,
      naturalLabel: `${m.defaultMultiplier ?? 0}x`,
    };
  });

  // Extended tiers extend the *destination* ladder only. They're never starting
  // points (brackets), since they're above the highest natural overtime tier.
  const highestBaseRate = allMultipliers.reduce(
    (max, m) => Math.max(max, m.defaultMultiplier ?? 0),
    0,
  );
  const normalizedExtended = mealBreakConfig.extendedOvertimeEnabled
    ? normalizeExtendedTiers(mealBreakConfig.extendedOvertimeTiers ?? [], highestBaseRate)
    : [];

  const baseLadder: OffsetLadderEntry[] = allMultipliers.map((m) => ({
    multiplier: m.defaultMultiplier ?? 0,
    label: `${m.defaultMultiplier ?? 0}x`,
  }));

  const extendedLadder: OffsetLadderEntry[] = normalizedExtended.map((tier) => ({
    multiplier: tier.rate,
    label: `${tier.rate}x`,
  }));

  const effectiveLadder: OffsetLadderEntry[] = [...baseLadder, ...extendedLadder];

  const baseLadderLength = allMultipliers.length;
  const maxOffset = Math.min(10, Math.max(1, baseLadderLength - 1));
  const offsetDisabled = baseLadderLength <= 1;

  const {
    highestOvertimeRate,
    highestOvertimeTierName,
    maxExtendedTiers,
  } = getOvertimeLadderInfo(rateMultipliers);

  const additionalControls = onMealConfigChange
    ? React.createElement(ExtendedOvertimeSection, {
        enabled: mealBreakConfig.extendedOvertimeEnabled ?? false,
        onEnabledChange: (v: boolean) => onMealConfigChange({ extendedOvertimeEnabled: v }),
        tiers: mealBreakConfig.extendedOvertimeTiers ?? [],
        onTiersChange: (tiers) => onMealConfigChange({ extendedOvertimeTiers: tiers }),
        highestOvertimeRate,
        highestOvertimeTierName,
        context,
        maxTiers: maxExtendedTiers,
        labelClassName: CONTROL_ROW_LABEL_CLASS,
        descriptionClassName: CONTROL_ROW_BODY_CLASS,
      })
    : null;

  const props: CountModeOffsetGraphicProps = {
    brackets,
    effectiveLadder,
    overtimeOffset: mealBreakConfig.overtimeOffset ?? 1,
    requiredBreaks: mealBreakConfig.requiredBreaks ?? null,
    maxOffset,
    offsetDisabled,
    ...(onMealConfigChange
      ? {
          onOvertimeOffsetChange: (value: number) =>
            onMealConfigChange({ overtimeOffset: value }),
        }
      : {}),
    ...(additionalControls ? { additionalControls } : {}),
  };

  const element = React.createElement(CountModeOffsetGraphic, props);

  return {
    injectables: { meal_count_offset_comparison: element },
  };
}
